<template>
  <form class="rental-form" @submit.prevent="handleSubmit">
    <div class="rental-form__summary">
      <BaseIcon name="cubes" size="16" />
      <div>
        <strong>{{ requirementLabel(requirement) }}</strong>
        <span v-if="requirement.work_order" class="rental-form__wo">
          {{ $t('resources.rental.for_work_order') }}
          {{ requirement.work_order.work_order_number || requirement.work_order.project_number }}
        </span>
      </div>
    </div>

    <div class="rental-form__row">
      <SelectInput
        id="rental_supplier"
        :label="$t('resources.rental.supplier')"
        v-model="form.rental_supplier_id"
        :options="supplierOptions"
      />
      <TextInput
        v-if="!form.rental_supplier_id"
        id="rental_supplier_name"
        :label="$t('resources.rental.supplier_name')"
        v-model="form.rental_supplier_name"
        placeholder="Boels, Loxam, …"
      />
    </div>

    <div class="rental-form__row">
      <NumberInput
        id="rental_cost"
        :label="$t('resources.rental.cost')"
        v-model="form.rental_cost"
        :min="0"
        placeholder="0,00"
      />
      <TextInput
        id="rental_reference"
        :label="$t('resources.rental.reference')"
        v-model="form.rental_reference"
        placeholder="Huurnr. / bon"
      />
    </div>

    <div class="rental-form__row">
      <DateInput
        id="rental_pickup_date"
        :label="$t('resources.rental.pickup_date')"
        v-model="form.rental_pickup_date"
      />
      <DateInput
        id="rental_return_date"
        :label="$t('resources.rental.return_date')"
        v-model="form.rental_return_date"
      />
    </div>

    <p class="rental-form__hint">
      <BaseIcon name="info" size="12" />
      {{ $t('resources.rental.create_task') }}
    </p>

    <div class="rental-form__actions">
      <Btn type="button" variant="outlined" @click="onCancel && onCancel()">
        {{ $t('common.cancel') }}
      </Btn>
      <Btn type="submit" variant="primary" :loading="saving">
        {{ $t('resources.rental.save') }}
      </Btn>
    </div>
  </form>
</template>

<script>
  import { defineComponent, ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { supplierService } from '@/services/supplierService';
  import { requirementLabel } from '@/constants/resourceConstants';
  import Btn from '@/components/partials/Btn.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import DateInput from '@/components/inputs/DateInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  const dateOnly = (value) => (value ? String(value).slice(0, 10) : '');

  export default defineComponent({
    name: 'RentalDetailsModal',
    components: { Btn, TextInput, SelectInput, NumberInput, DateInput, BaseIcon },
    props: {
      requirement: { type: Object, required: true },
      onSubmit: { type: Function, required: true },
      onCancel: { type: Function, default: null },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const saving = ref(false);
      const suppliers = ref([]);
      const r = props.requirement;

      const defaultPickup = () => {
        if (r.rental_pickup_date) return dateOnly(r.rental_pickup_date);
        return dateOnly(r.from_date) || '';
      };
      const defaultReturn = () => {
        if (r.rental_return_date) return dateOnly(r.rental_return_date);
        return dateOnly(r.to_date) || '';
      };

      const form = ref({
        rental_supplier_id: r.rental_supplier_id || '',
        rental_supplier_name: r.rental_supplier_name || '',
        rental_cost: r.rental_cost ?? '',
        rental_reference: r.rental_reference || '',
        rental_pickup_date: defaultPickup(),
        rental_return_date: defaultReturn(),
      });

      const supplierOptions = computed(() => [
        { value: '', label: $t('resources.rental.supplier_other') },
        ...suppliers.value.map((s) => ({ value: s.id, label: s.name })),
      ]);

      onMounted(async () => {
        try {
          const response = await supplierService.getAllSuppliers({ limit: 100 });
          suppliers.value = response.suppliers || response.data || [];
        } catch {
          suppliers.value = [];
        }
      });

      const handleSubmit = async () => {
        saving.value = true;
        try {
          const f = form.value;
          const supplier = suppliers.value.find((s) => s.id === f.rental_supplier_id);
          await props.onSubmit({
            status: r.status === 'required' || r.status === 'reserved' ? 'rented' : r.status,
            fulfillment: 'rent',
            rental_supplier_id: f.rental_supplier_id || null,
            rental_supplier_name: supplier ? supplier.name : f.rental_supplier_name?.trim() || null,
            rental_cost:
              f.rental_cost === '' || f.rental_cost === null ? null : Number(f.rental_cost),
            rental_reference: f.rental_reference?.trim() || null,
            rental_pickup_date: f.rental_pickup_date || null,
            rental_return_date: f.rental_return_date || null,
          });
        } finally {
          saving.value = false;
        }
      };

      return { form, saving, supplierOptions, handleSubmit, requirementLabel };
    },
  });
</script>

<style scoped lang="scss">
  .rental-form {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;

    &__summary {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      padding: 0.625rem 0.75rem;
      border-radius: 8px;
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
      color: var(--slate-600);

      strong {
        display: block;
        color: var(--slate-900);
        font-size: 0.875rem;
      }
    }

    &__wo {
      font-size: 0.75rem;
    }

    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__hint {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      margin: 0;
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-200);
    }
  }
</style>
